import type { CommunityMarker } from '@hotlist/shared';

/**
 * What everybody else says is out there.
 *
 * Held apart from what this player has personally seen, and drawn differently,
 * because the difference between the two is the thing worth learning. Your own
 * map is a record of where you have driven. This one is a record of where
 * *people* have driven, which is a different and much larger claim with much
 * weaker evidence behind every individual point.
 *
 * A marker here may be a pole that came down last month. Nothing corrects that
 * except somebody going to look, which is exactly the situation the real
 * crowdsourced maps are in and exactly why the confidence vocabulary exists.
 */
export class CommunityHeld {
  private markers: CommunityMarker[] = [];
  private contributed = 0;

  set(markers: CommunityMarker[], yours: number): void {
    this.markers = markers;
    this.contributed = yours;
  }

  all(): readonly CommunityMarker[] {
    return this.markers;
  }

  get size(): number {
    return this.markers.length;
  }

  get yours(): number {
    return this.contributed;
  }

  /**
   * How many of these are cameras this player has not found.
   *
   * The only number that matters when the map goes up: the rest are their own
   * reports coming back to them.
   */
  newTo(mine: readonly { x: number; y: number }[], within = 25): number {
    return this.markers.filter(
      (marker) => !mine.some((c) => Math.hypot(c.x - marker.x, c.y - marker.y) < within),
    ).length;
  }

  /** A sentence a player can read off the panel. */
  summary(mine: readonly { x: number; y: number }[] = []): string {
    if (this.markers.length === 0) {
      return 'Nobody has reported a camera yet. Yours are the only ones on here.';
    }
    const fresh = this.newTo(mine);
    if (fresh === 0) {
      return `${this.markers.length} reported, and you had already found all of them.`;
    }
    const counts = new Map<string, number>();
    for (const marker of this.markers) counts.set(marker.c, (counts.get(marker.c) ?? 0) + 1);
    const parts = ['field-verified', 'persistent', 'corroborated', 'unconfirmed']
      .filter((key) => counts.has(key))
      .map((key) => `${counts.get(key)} ${key}`);
    return `${fresh} camera${fresh === 1 ? '' : 's'} you had not found. ${parts.join(', ')}.`;
  }
}
